import { CreateLabelResponse, LABELS_URL } from 'api/LabelsApi';
import { AxiosResponse } from 'axios';
import { AddLabel, DeleteLabel, EditLabel } from 'modules/projectSettings/types';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { api, ApiMethod } from 'services/api';
import noop from 'utils/noop';
import { ProjectLabel } from '../types';
import { getManagedLabels } from '../utils/labels';
import { useBatchMutation } from './useBatchMutation';

export const useManageLabels = (onComplete: () => void = noop) => {
  const dispatch = useDispatch();
  const intialCompleteState = { add: false, edit: false, delete: false };
  const [isComplete, setIsComplete] = useState(intialCompleteState);

  // utils
  const createOnError = (action: string, source: string): ((error?: unknown) => void) => {
    return (error: unknown) => {
      dispatch(
        addNegativeToastWithErrorId(`Unable to ${action} label. Please try again.`, source, error),
      );
    };
  };

  const createRequest = <Item>(url: string, method: ApiMethod, data: Item) =>
    api.request({ url, method, data });

  /** Add Labels */
  const { mutate: addLabels } = useBatchMutation<AddLabel, AxiosResponse<CreateLabelResponse>>({
    mutationFn(item) {
      return createRequest(LABELS_URL, ApiMethod.post, item);
    },
    onSuccess: () => {
      setIsComplete((prev) => ({ ...prev, add: true }));
    },
    onError: createOnError('add', 'useCreateLabel'),
  });

  /** Edit Labels */
  const { mutate: editLabels } = useBatchMutation<EditLabel, AxiosResponse<string>>({
    mutationFn(item) {
      return createRequest(`${LABELS_URL}/${item.id}`, ApiMethod.post, item);
    },
    onSuccess: () => {
      setIsComplete((prev) => ({ ...prev, edit: true }));
    },
    onError: createOnError('edit', 'useEditLabels'),
  });

  /** Delete Labels */
  const { mutate: deleteLabels } = useBatchMutation<DeleteLabel, AxiosResponse<string>>({
    mutationFn(item) {
      return createRequest(`${LABELS_URL}/${item.id}`, ApiMethod.delete, item);
    },
    onSuccess: () => {
      setIsComplete((prev) => ({ ...prev, delete: true }));
    },
    onError: createOnError('delete', 'useDeleteLabels'),
  });

  /** Handle Managed Labels: utility function to add, edit and delete lables */
  const handleManageLabels = (values: ProjectLabel[], initialLabels: ProjectLabel[]) => {
    /** Find all labels that have been created, updated, or deleted */
    const { newLabels, updatedLabels, deletedLabels } = getManagedLabels(values, initialLabels);

    /** Add Labels */
    if (newLabels.length > 0) {
      addLabels(newLabels);
    } else {
      setIsComplete((prev) => ({ ...prev, add: true }));
    }

    /** Update labels */
    if (updatedLabels.length > 0) {
      editLabels(updatedLabels);
    } else {
      setIsComplete((prev) => ({ ...prev, edit: true }));
    }

    /** Delete labels */
    if (deletedLabels.length > 0) {
      deleteLabels(deletedLabels);
    } else {
      setIsComplete((prev) => ({ ...prev, delete: true }));
    }
  };

  useEffect(() => {
    // Once all label operations are complete, refetch labels and call onComplete from parent
    if (isComplete.add && isComplete.edit && isComplete.delete) {
      onComplete();
      setIsComplete(intialCompleteState);
      /** Show notification on completion, function only runs if there are Label changes */
      dispatch(addPositiveToast('Labels updated successfully.'));
    }
  }, [dispatch, isComplete, onComplete]);

  return { handleManageLabels };
};
